import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import SEO from "../components/SEO";
import { DOWNLOAD_URL } from "../data/siteCatalog";
import "../style/SynapseInvitePage.css";

const INVITE_CODE_PATTERN = /^SYNMAIL(?:-[A-HJ-NP-Z2-9]{6}){4}$/;

function normalizeInviteCode(value) {
  return String(value || "").trim().toUpperCase().replace(/\s+/g, "-");
}

async function copyText(value) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("copy_failed");
}

export default function SynapseInvitePage() {
  const [searchParams] = useSearchParams();
  const [copyStatus, setCopyStatus] = useState("");
  const inviteCode = useMemo(
    () => normalizeInviteCode(searchParams.get("code")),
    [searchParams]
  );
  const isValidCode = INVITE_CODE_PATTERN.test(inviteCode);

  const handleCopy = async () => {
    try {
      await copyText(inviteCode);
      setCopyStatus("Codice copiato. Conservalo per Synapse.");
    } catch (_error) {
      setCopyStatus("Copia il codice manualmente prima di continuare.");
    }
  };

  return (
    <section className="synapse-invite" aria-labelledby="synapse-invite-title">
      <SEO
        title="Invito a Synapse | Kyros Games"
        description="Completa il tuo invito a Synapse creando un account Kyros, installando il launcher e riscattando il codice nel gioco."
        canonical="https://kyrosgames.com/synapse/invite"
        robots="noindex,nofollow"
      />

      <div className="synapse-invite__signal" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>

      <div className="synapse-invite__layout">
        <header className="synapse-invite__hero">
          <p className="synapse-invite__eyebrow">Trasmissione privata · Rete Synapse</p>
          <h1 id="synapse-invite-title">Qualcuno vuole averti nella sua rete.</h1>
          <p className="synapse-invite__lead">
            Non serve avere già il gioco. Il percorso parte da qui e termina con una
            richiesta di amicizia che sarai tu a decidere se accettare.
          </p>
        </header>

        <aside className={`synapse-invite__card${isValidCode ? "" : " synapse-invite__card--invalid"}`}>
          <p className="synapse-invite__card-label">Codice di collegamento</p>
          {isValidCode ? (
            <>
              <code className="synapse-invite__code">{inviteCode}</code>
              <button type="button" className="synapse-invite__copy" onClick={handleCopy}>
                Copia codice
              </button>
              <p className="synapse-invite__copy-status" role="status" aria-live="polite">
                {copyStatus || "Il codice viene verificato in modo sicuro dentro Synapse."}
              </p>
            </>
          ) : (
            <div className="synapse-invite__invalid" role="alert">
              Questo link non contiene un codice valido. Chiedi al mittente di inviare un
              nuovo invito.
            </div>
          )}
        </aside>

        <div className="synapse-invite__steps" aria-label="Come accettare l'invito">
          <article>
            <span>01</span>
            <h2>Crea il profilo Kyros</h2>
            <p>Registrati gratuitamente oppure accedi se hai già un account.</p>
            <Link to="/account/register">Crea account</Link>
          </article>
          <article>
            <span>02</span>
            <h2>Installa il launcher</h2>
            <p>È il punto ufficiale per scaricare, aggiornare e avviare Synapse.</p>
            <a href={DOWNLOAD_URL} download>Scarica per Windows</a>
          </article>
          <article>
            <span>03</span>
            <h2>Riscatta nel gioco</h2>
            <p>Apri Amici, incolla il codice e conferma. Poi scegli se accettare la richiesta.</p>
            <Link to="/store">Vai allo Store Kyros</Link>
          </article>
        </div>

        <footer className="synapse-invite__trust">
          <strong>Nessuna amicizia automatica.</strong>
          <span>Il codice è monouso, scade e non assegna premi o valuta.</span>
        </footer>
      </div>
    </section>
  );
}
